<template>
  <div ref="threeContainer" class="three-container relative">
    <!-- 加载遮罩层 -->
    <div
      v-if="isLoading"
      class="loading-overlay absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 text-white z-10 transition-all duration-300"
    >
      <svg
        class="animate-spin w-10 h-10 mb-4 text-purple-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <p class="text-lg font-medium">{{ $t('common.vrmModelLoading') }}</p>
      <p class="text-sm mt-1">{{ Math.round(loadingProgress * 100) }}%</p>
    </div>

    <!-- Three.js 渲染容器 -->
    <div ref="rendererContainer" class="w-full h-full"></div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { VRM, VRMUtils, VRMLoaderPlugin } from '@pixiv/three-vrm';
import { getModelUrl } from '@/utils/index';

export default {
  name: 'VrmModel',
  props: {
    modelPath: {
      type: String,
      required: true
    },
    cameraDistance: {
      type: Number,
      default: 5
    },
    enableControls: {
      type: Boolean,
      default: true
    },
    scale: {
      type: Object,
      default: () => ({ x: 1, y: 1, z: 1 })
    },
    position: {
      type: Object,
      default: () => ({ x: 0, y: 0, z: 0 })
    },
    rotation: {
      type: Object,
      default: () => ({ x: 0, y: 0, z: 0 })
    },
    background: {
      type: String,
      default: '#333333'
    },
    shouldLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      vrm: null,
      loader: null,
      isLoading: false,
      loadingProgress: 0,
      clock: new THREE.Clock()
    };
  },
  async mounted() {
    if (!this.modelPath) {
      console.error('VrmModel: modelPath is required.');
      this.$emit('error', new Error('modelPath is required'));
      return;
    }

    if (this.shouldLoad) {
      this.startLoading();
    }
  },
  beforeDestroy() {
    this.cleanUp();
  },
  methods: {
    startLoading() {
      try {
        this.isLoading = true;
        this.initThree();
        this.loadVrmModel(this.modelPath);
        this.animate();
      } catch (error) {
        console.error('Failed to initialize VrmModel:', error);
        this.$emit('error', error);
        this.isLoading = false;
      }
    },
    initThree() {
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(this.background);

      const width = this.$refs.threeContainer.clientWidth;
      const height = this.$refs.threeContainer.clientHeight;

      this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
      this.camera.position.set(0, 1.4, this.cameraDistance);
      this.camera.lookAt(0, 1.4, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);

      this.$refs.rendererContainer.appendChild(this.renderer.domElement);

      // 光源
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      this.scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
      directionalLight.position.set(1, 1, 1);
      this.scene.add(directionalLight);

      if (this.enableControls) {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.target.set(0, 1.4, 0);
      }

      window.addEventListener('resize', this.onWindowResize);
    },

    loadVrmModel(vrmFileUrl) {
      this.loader = new GLTFLoader();

      // 使用 VRMLoaderPlugin 让 GLTFLoader 支持 .vrm
      this.loader.register((parser) => new VRMLoaderPlugin(parser));

      // 处理 URL，解决 CORS 问题
      const processedUrl = getModelUrl(vrmFileUrl);

      this.loader.load(
        processedUrl,
        (gltf) => {
          // 从 gltf.userData 中直接拿到 VRM 实例（由 VRMLoaderPlugin 提供）
          const vrm = gltf.userData.vrm;
          if (!vrm) {
            console.error('VRM model not found in gltf.userData.vrm');
            this.$emit('error', new Error('VRM model not found'));
            this.isLoading = false;
            return;
          }

          console.log('VRM loaded:', vrm);

          // 可选：优化 VRM 模型
          try {
            VRMUtils.removeUnnecessaryJoints(vrm.scene);
          } catch (e) {
            // 老版本 three-vrm 可能没有这个方法，忽略错误
            console.warn('VRMUtils.removeUnnecessaryJoints failed:', e);
          }

          this.vrm = vrm;

          const object = vrm.scene;
          object.scale.set(this.scale.x, this.scale.y, this.scale.z);
          object.position.set(this.position.x, this.position.y, this.position.z);
          object.rotation.set(this.rotation.x, this.rotation.y, this.rotation.z);

          this.scene.add(object);
          this.fitCameraToObject(object, this.cameraDistance);

          this.isLoading = false;
          this.loadingProgress = 1;
          this.$emit('loaded');
        },
        (xhr) => {
          if (xhr.total) {
            this.loadingProgress = xhr.loaded / xhr.total;
            this.$emit('progress', this.loadingProgress);
          }
        },
        (error) => {
          console.error('An error happened loading VRM:', error);
          this.$emit('error', error);
          this.isLoading = false;
        }
      );
    },

    fitCameraToObject(object, offset = 1.25) {
      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);

      if (maxDim === 0 || !isFinite(maxDim) || maxDim > 10000) {
        console.warn('VRM model size is invalid or excessively large:', maxDim);
        this.camera.position.set(0, 1.4, 3);
        this.camera.lookAt(0, 1.4, 0);
        this.camera.updateProjectionMatrix();
        return;
      }

      const fov = this.camera.fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
      cameraZ *= offset;

      const neededFar = cameraZ * 3;
      if (this.camera.far < neededFar) {
        this.camera.far = Math.max(neededFar, 1000);
        this.camera.updateProjectionMatrix();
      }

      this.camera.position.set(center.x, center.y + size.y * 0.1, center.z + cameraZ);
      this.camera.lookAt(center.x, center.y + size.y * 0.4, center.z);
      this.camera.updateProjectionMatrix();

      if (this.controls) {
        this.controls.target.copy(center);
      }
    },

    animate() {
      requestAnimationFrame(this.animate);

      const delta = this.clock.getDelta();

      if (this.vrm) {
        this.vrm.update(delta);
      }

      if (this.controls) {
        this.controls.update();
      }

      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    },

    onWindowResize() {
      const width = this.$refs.threeContainer.clientWidth;
      const height = this.$refs.threeContainer.clientHeight;

      if (!width || !height || !this.camera || !this.renderer) return;

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    },

    cleanUp() {
      window.removeEventListener('resize', this.onWindowResize);

      if (this.renderer) {
        this.renderer.dispose();
        this.renderer.forceContextLoss && this.renderer.forceContextLoss();
        if (this.$refs.rendererContainer && this.renderer.domElement.parentNode) {
          this.$refs.rendererContainer.removeChild(this.renderer.domElement);
        }
      }

      if (this.controls) {
        this.controls.dispose();
        this.controls = null;
      }

      if (this.vrm) {
        if (this.scene) {
          this.scene.remove(this.vrm.scene);
        }
        this.disposeObject(this.vrm.scene);
        this.vrm = null;
      }
    },

    disposeObject(object) {
      if (!object) return;
      object.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => this.disposeMaterial(mat));
            } else {
              this.disposeMaterial(child.material);
            }
          }
        }
      });
    },

    disposeMaterial(material) {
      if (!material) return;
      const maps = [
        'map', 'lightMap', 'aoMap', 'emissiveMap', 'bumpMap',
        'normalMap', 'displacementMap', 'roughnessMap',
        'metalnessMap', 'alphaMap'
      ];
      maps.forEach(map => {
        if (material[map]) {
          material[map].dispose();
        }
      });
      material.dispose();
    }
  },
  watch: {
    modelPath: {
      handler(newPath) {
        if (!newPath) return;

        if (this.vrm && this.scene) {
          this.scene.remove(this.vrm.scene);
          this.disposeObject(this.vrm.scene);
          this.vrm = null;
        }

        if (this.shouldLoad) {
          this.isLoading = true;
          this.loadVrmModel(newPath);
        }
      },
      immediate: false
    },
    shouldLoad: {
      handler(newVal) {
        if (newVal && this.modelPath && !this.vrm) {
          this.startLoading();
        }
      },
      immediate: false
    }
  }
};
</script>

<style scoped>
.three-container {
  width: 100%;
  height: 100%;
  background-color: #232A3E;
  position: relative;
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}
</style>


